import { adminApiUrl } from '../services/api';
import { useState } from 'react';
import type { AdminStaffRole } from '@lighthouse/shared';
import { useAdminAuth, type AdminUser } from '../hooks/useAdminAuth';
import { AuthShell } from '../components/auth/AuthShell';

const REMEMBER_EMAIL_KEY = 'lighthouse-admin-remember-email';

function toAdminUser(user: {
  id: string;
  email: string;
  role: string;
  adminStaffRole?: AdminStaffRole;
}): AdminUser {
  return {
    id: user.id,
    email: user.email,
    role: user.role,
    ...(user.adminStaffRole ? { adminStaffRole: user.adminStaffRole } : {}),
  };
}

export function LoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) ?? '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [otp, setOtp] = useState('');
  const [mfaSessionId, setMfaSessionId] = useState<string | null>(null);
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();

  const storeRememberedEmail = () => {
    if (remember) localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
    else localStorage.removeItem(REMEMBER_EMAIL_KEY);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(adminApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Type': 'admin' },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe: remember }),
      });

      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        user?: { id: string; email: string; role: string; adminStaffRole?: AdminStaffRole };
        mfaRequired?: boolean;
        mfaSessionId?: string;
        tokens?: { accessToken?: string };
      };

      if (!response.ok) {
        throw new Error(data.message ?? 'Invalid email/phone or password');
      }

      if (data.mfaRequired && data.mfaSessionId) {
        storeRememberedEmail();
        setMfaSessionId(data.mfaSessionId);
        setStep('otp');
        return;
      }

      if (!data.tokens?.accessToken) {
        setError('Login failed — no token received');
        return;
      }

      if (!data.user || data.user.role !== 'admin') {
        setError('Invalid email/phone or password');
        return;
      }

      storeRememberedEmail();
      login(toAdminUser(data.user), data.tokens.accessToken);
    } catch (err) {
      setError((err as Error).message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaSessionId) return;
    setError('');
    setLoading(true);

    try {
      const response = await fetch(adminApiUrl('/auth/mfa/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Type': 'admin' },
        credentials: 'include',
        body: JSON.stringify({ mfaSessionId, otp }),
      });

      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        user?: { id: string; email: string; role: string; adminStaffRole?: AdminStaffRole };
        tokens?: { accessToken?: string };
      };
      if (!response.ok) {
        throw new Error(data.message ?? 'Invalid email/phone or password');
      }

      if (!data.tokens?.accessToken) {
        setError('OTP verification failed — no token received');
        return;
      }

      if (!data.user || data.user.role !== 'admin') {
        setError('Invalid email/phone or password');
        return;
      }

      login(toAdminUser(data.user), data.tokens.accessToken);
    } catch (err) {
      setError((err as Error).message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={step === 'login' ? 'Admin sign in' : 'Verify your identity'}
      subtitle={
        step === 'login'
          ? 'Secure access to Mylighthouse operations.'
          : 'Enter the one-time code sent to your registered device.'
      }
    >
      {step === 'login' ? (
        <form onSubmit={handleLogin} className="space-y-4">
          {error && <div className="auth-error-banner">{error}</div>}
          <div>
            <label className="auth-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="admin@mylighthouse.ng"
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label className="auth-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-primary-600 h-4 w-4 rounded border-stone-300"
            />
            Remember me
          </label>
          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleOtpVerify} className="space-y-4">
          {error && <div className="auth-error-banner">{error}</div>}
          <p className="text-sm text-stone-500">Enter the OTP sent to your registered device</p>
          <div>
            <label className="auth-label">One-time password</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="input-field text-center text-2xl tracking-widest"
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
            />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? 'Verifying…' : 'Verify OTP'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
